/**
 * TriSys Authentication Service
 * Reads session from Flexiva's central auth and provides headers with tenant SiteKey
 * 
 * Generated from: TriSys-2026.01.13.postman_collection.json
 * Date: 2026-01-14
 */

import { Logger } from '@/services/Logger';
import { oemConfig } from '@/services/OEMConfig';

const logger = Logger.scope('TriSysAuth');

// Use the SAME session key as Flexiva's central TriSysWebAPI
const SESSION_KEY = 'trisys_data_services_key';

// Fallback SiteKey if OEM config not loaded (should not happen in production)
const FALLBACK_SITE_KEY = '20131222-ba09-4e4c-8bcb-apex.web.app';

/**
 * TriSys uses dual-header authentication:
 * - SiteKey: Static API key identifying the tenant
 * - DataServicesKey: Session token from Flexiva's central authentication
 */
class AuthService {
  
  /**
   * Get the static SiteKey for this tenant from OEM config
   * CRITICAL: Each tenant has its own unique SiteKey configured in oem.json
   */
  getSiteKey(): string {
    // Read from OEM config - this ensures tenant forms use same SiteKey as central auth
    const siteKey = oemConfig.getSiteKey();
    if (siteKey) {
      return siteKey;
    }
    // Fallback for edge cases (should not happen)
    logger.warn('SiteKey not found in OEM config, using fallback');
    return FALLBACK_SITE_KEY;
  }

  /**
   * Get the current session's DataServicesKey from Flexiva's central storage
   */
  getSessionKey(): string | null {
    // Read from session storage (Flexiva's central auth uses this)
    return sessionStorage.getItem(SESSION_KEY);
  }

  /**
   * Check if user is authenticated (via Flexiva's central auth)
   */
  isAuthenticated(): boolean {
    return !!this.getSessionKey();
  }

  /**
   * Get authentication headers for API requests
   * Includes both tenant SiteKey and session DataServicesKey
   */
  getHeaders(): Record<string, string> {
    const headers: Record<string, string> = {
      'Content-Type': 'application/json',
      'SiteKey': this.getSiteKey()
    };
    
    const sessionKey = this.getSessionKey();
    if (sessionKey) {
      headers['DataServicesKey'] = sessionKey;
    }
    
    return headers;
  }

  /**
   * Get headers for authentication request (SiteKey only)
   */
  getAuthHeaders(): Record<string, string> {
    return {
      'Content-Type': 'application/json',
      'SiteKey': this.getSiteKey()
    };
  }
}

// Export singleton instance
export const trisysAuth = new AuthService();
